<script setup lang="ts">
import { CloseHotelSelectionFiltersOrigin } from '@apps/main/new-desktop/core/models';
import type { HotelIngredient } from '@apps/main/modules/recipe/ingredients';
import type { OwnStayIngredient } from '@apps/main/modules/recipe/ingredients/ownStayIngredient';

import { AppModal, AppButton, AppLinkButton } from '@libs/ui';

import type { HotelFilterUpdate } from './model/hotelFilters';
import HotelFilters from './HotelFilters.vue';

interface HotelFiltersModalProps {
  ingredient: HotelIngredient | OwnStayIngredient | null;
  disableHotelNameFilter?: boolean;
}

const props = defineProps<HotelFiltersModalProps>();

interface HotelFiltersModalEmits {
  (eventName: 'close', origin: CloseHotelSelectionFiltersOrigin): void;
  (eventName: 'update-filter', form: HotelFilterUpdate): void;
  (eventName: 'reset-filter'): void;
  (eventName: 'apply-filter'): void;
}

const emit = defineEmits<HotelFiltersModalEmits>();

function onUpdateFilter(filterUpdate: HotelFilterUpdate): void {
  emit('update-filter', filterUpdate);
}
</script>

<template>
  <AppModal
    max-height="654px"
    max-width="600px"
    :close-button-aria-label="$t('a11y.close_panel')"
    @close="emit('close', CloseHotelSelectionFiltersOrigin.CloseButton)"
  >
    <section class="hotel-filters-modal">
      <main class="top-section">
        <HotelFilters
          theme="dark"
          :ingredient="props.ingredient"
          :max-length="25"
          :disable-hotel-name-filter="props.disableHotelNameFilter"
          @update-filter="onUpdateFilter"
        />
      </main>
      <footer class="footer">
        <div class="buttons-container">
          <div class="reset-button">
            <AppLinkButton
              class="reset-filters-button"
              :text="$t('planner.reset_hotel_filters')"
              size="m"
              state="enabled"
              @click="emit('reset-filter')"
            />
          </div>
          <div class="close-and-apply-button">
            <AppButton
              state="enabled"
              class="button"
              type="secondary"
              :wide="true"
              size="large"
              @click="
                emit('close', CloseHotelSelectionFiltersOrigin.CloseButton)
              "
            >
              {{ $t('planner.close') }}
            </AppButton>
            <AppButton
              state="enabled"
              class="button"
              type="primary"
              :wide="true"
              size="large"
              @click="emit('apply-filter')"
            >
              {{ $t('planner.done') }}
            </AppButton>
          </div>
        </div>
      </footer>
    </section>
  </AppModal>
</template>

<style lang="scss" scoped>
.hotel-filters-modal {
  display: flex;
  flex-direction: column;
  height: 100%;
  .top-section {
    flex: 1;
    overflow-y: auto;
    padding: var(--spacing-32) var(--spacing-24) var(--spacing-24)
      var(--spacing-24);
    min-height: 0;
  }
  .footer {
    display: flex;
    flex-direction: row;
    flex-shrink: 0;
    padding: var(--spacing-24);
    border-top: 1px solid var(--color-neutral-30);
    justify-content: flex-end;
    .buttons-container {
      display: flex;
      flex-direction: row;
      width: 100%;
      justify-content: space-between;

      .reset-button {
        margin: auto 0;
      }
      .close-and-apply-button {
        width: 266px;
        display: flex;
        flex-direction: row;
        gap: var(--spacing-16);
      }
    }
  }
}
</style>
